import * as React  from "react";
import {XdSentry}  from "@Common/libs/sentry/xd-sentry";
import {Types}     from "@Common/types";

import {cst, cstAssign} from "@Common/react/chain-services";
import {Load2}          from "@Common/react/load2";
import {consumeContext} from "@Common/react/context";
import {NoPermit}           from "@Xpresso/routes/account/common/bulk-mail-permit/no-permit/no-permit";

import {Modal as FormModal} from "./form-modal";

interface VarStack {
    apis             : Types.Apis.Loaders.XpressoLoadApis,
    session          : Types.Apis.Loaders.LoadSessionContextNext,
    company          : any,
    formModal        : Types.Xpresso.Account.Office.AddressBookRoute.ModalNextIndiciaFormModal,
    handleFormSubmit : (v:Types.Xpresso.Account.Office.AddressBookRoute.Indicia) => Promise<any>,
    recordLoad       : Types.Common.React.Load2.Load2Next<Types.Xpresso.Account.Office.AddressBookRoute.Indicia>,
}

export const Component = ({
    next
}:Types.Xpresso.Account.Office.AddressBookRoute.ComponentPropsIndiciaSection) => cst<VarStack>(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("session"),

    cstAssign("recordLoad", ({apis}, next) => Load2({
        next,
        fetch: apis.next.bulkMailPermit.getIndica,
    })),

    cstAssign("handleFormSubmit", ({apis, recordLoad}, next) => next(async (updatedValue) => {
        try {
            await apis.next.bulkMailPermit.saveIndicia({indicia: updatedValue});

            const empty = (
                ["line1", "line2", "line3", "line4", "line5"]
                .map(line => updatedValue[line])
                .filter(Boolean)
                .length === 0
            );
            recordLoad.onChange(empty ? null : updatedValue);
        } catch (error) {
            if ((typeof error)=="string")
                return {errorMessage: error};
            else {
                XdSentry.captureException(error);
                return {errorMessage: "An unexpected error occurred."};
            }
        }
    })),

    cstAssign("formModal", ({handleFormSubmit, recordLoad}, next) => FormModal({
        next,
        onSubmit : handleFormSubmit,
        value    : recordLoad.value,
    })),

    ({company, formModal, recordLoad, session}) => next({
        isLoading : recordLoad.loading,
        render    : () => Content({
            indicia     : recordLoad.value,
            isVisible   : indiciaAllowed(
                Number(company?.prefs?.allowBulkMailPermit),
                Number(session?.data?.branch?.allowBulkMailPermit),
            ),
            onLinkClick : formModal.show,
        }),
    }),
);

function indiciaAllowed(companyPref:number, branchPref:number=0):boolean
{
    return Boolean((companyPref & (branchPref >> 1)) | (branchPref & 1));
}

interface ContentProps {
    indicia     : Types.Xpresso.Account.Office.AddressBookRoute.Indicia,
    isVisible   : boolean,
    onLinkClick : React.MouseEventHandler,
}

const Content = ({indicia, isVisible, onLinkClick: handleLinkClick}:ContentProps) => isVisible && (
    <div key="indicia" className="section">
        <div className="header">
            <h3 className="title" xd-qa-type="label" xd-qa-name="Manage Bulk Mail Permit">Manage Bulk Mail Permit</h3>
            <a onClick={handleLinkClick} xd-qa-type="link" xd-qa-name={`${indicia === null ? "Add" : "Edit"} Bulk Mail Permit`} className="edit">{indicia === null ? "Add" : "Edit"}</a>
        </div>
        
        <div className="card indicia-card">
            {
                (indicia !== null)
                ?
                <div className="permit-active">
                    {
                        ["line1", "line2", "line3", "line4", "line5"].map(lineName => indicia[lineName]).filter(Boolean).map((line, index) => (
                            <div key={index} xd-qa-type="label" xd-qa-name={`Line ${index + 1}`} className="value">
                                {line}
                            </div>
                        ))
                    }
                </div>
                :
                <div className="no-permit">
                    {               
                        NoPermit()
                    }
                </div>                
            }             
        </div>     
        
    </div>
);
